import { WrappedFooter } from "@/comps/footer"
import Header from "@/comps/header"
import { CustomThemedResource } from "@/comps/image"
import Meta from "@/comps/meta"
import { PlayWithProvider, Timeline } from "@/comps/player"
import { HEADER_WEB_VERSION } from "@/utils/cdn"
import { base, code } from "@/utils/queryfy"
import { SSRNotFound, wrapSSRAuth } from "@/utils/ssr-auth"
import { APP_HOST } from "@/utils/variables"
import axios from "axios"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import {
  adExampleEntrySchema,
  AdExamplesSingle,
} from "pages/ad-examples/index"
import { useEffect, useRef, useState } from "react"
import Masonry, {
  ResponsiveMasonry,
} from "react-responsive-masonry"
import { VideoObject, WithContext } from "schema-dts"
import { SingleAd } from "sections/gallery/ad-examples"
import {
  ScriptSection,
  SessionName,
  ShareSection,
  TryNowButton,
  UsernameFollowSection,
} from "sections/gallery/ad-examples-popup"
import { VideoProvider } from "video-provider"
import { z } from "zod"

function getJsonLdData(
  entity: AdExamplesSingle,
): WithContext<VideoObject>[] | undefined {
  if (!entity.preview_video_url) {
    return undefined
  }

  return [
    {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: entity.title,
      description: entity.description,
      thumbnailUrl: [entity.preview_thumbnail_url],
      license: "https://zoomerang.app/privacy-policy",
      acquireLicensePage:
        "https://zoomerang.app/ad-examples/single/" +
        entity.id,
      copyrightNotice: "Zoomerang",
      uploadDate: entity.created_at,
      author: {
        "@type": "Person",
        name: entity.owner?.username ?? "Zoomerang",
      },
      creator: {
        "@type": "Person",
        name: entity.owner?.username ?? "Zoomerang",
      },
      contentUrl: entity.preview_video_url,
    },
  ]
}

interface SingleProps {
  job: ResultResponse
  related: RelatedType
}
export default function ChatSinglePage(props: SingleProps) {
  const { job, related } = props
  const {
    preview_thumbnail_url,
    aspect,
    description,
    preview_video_url,
    share_url,
    created_at,
    title,
    id,
    owner,
  } = job
  const videoRef = useRef<HTMLVideoElement>(null)
  const { t } = useTranslation()
  const [downloadApi, setDownloadApi] = useState(false)
  const [clicked, setClicked] = useState(false)
  const router = useRouter()

  useEffect(() => {
    if (window.history.length <= 1 && clicked) {
      router.push("/ad-examples")
    } else if (clicked) {
      router.back()
    }
  }, [clicked, router])

  useEffect(() => {
    if (downloadApi) return
    const isIOS = /iPad|iPhone|iPod/.test(
      navigator.userAgent,
    )
    const isSafari = /^((?!chrome|android).)*safari/i.test(
      navigator.userAgent,
    )

    if (isIOS || isSafari) {
      setDownloadApi(true)
      return
    }
  }, [downloadApi])

  const thumbnail = preview_thumbnail_url.startsWith(
    "https://",
  )
    ? preview_thumbnail_url
    : undefined

  return (
    <div className="tablet:min-h-screen">
      <Meta
        title={
          (title ?? "") + " Advertising Example - Zoomerang"
        }
        description={description}
        cover={thumbnail}
        jsonLdData={getJsonLdData(job)}
      />
      <div className="hidden desktop:block">
        <Header />
      </div>

      <div className="h-[100svh] w-screen tablet:h-auto">
        <div className="relative flex w-full flex-col bg-color-popup-cell desktop:mx-auto desktop:w-[1100px] desktop:bg-[transparent] desktop:pt-8">
          {/* Back Button */}
          <button
            onClick={(e) => {
              e.preventDefault()
              setClicked(true)
            }}
            className="hidden items-center desktop:flex">
            <CustomThemedResource
              source="/general/arrow"
              format="svg"
              alt="close icon"
              className="h-[24px] w-[24px] rotate-180"
            />
            <span className="text-[18px] font-500 text-blue-700">
              {t("lbl_back")}
            </span>
          </button>
          <button
            onClick={(e) => {
              e.preventDefault()
              setClicked(true)
            }}
            className={clsx(
              "absolute left-4 top-4 z-20 h-10 w-10 rounded-full bg-color-white/20 tablet:h-10 tablet:w-10",
              "flex items-center justify-center desktop:hidden",
            )}>
            <div className="relative h-4 w-4 rotate-45">
              <div className="absolute left-1/2 top-0 h-4 w-[2px] -translate-x-[1px] rounded-sm bg-color-white" />
              <div className="absolute left-0 top-1/2 h-[2px] w-4 -translate-y-[1px] rounded-sm bg-color-white" />
            </div>
          </button>

          {/* Content Section */}
          <div className="flex w-full flex-col desktop:mt-7 desktop:flex-row">
            {/* Video Section */}
            <div className="relative flex flex-1 items-center justify-center desktop:items-start">
              <div
                className={clsx(
                  "absolute inset-0 h-full w-full bg-color-black filter",
                  "pointer-events-none hidden select-none overflow-hidden tablet:block desktop:hidden",
                )}>
                <img
                  src={thumbnail}
                  alt="preview image"
                  className="h-full w-full scale-110 object-cover opacity-30 blur-lg"
                />
              </div>

              {preview_video_url ? (
                <div
                  style={{
                    aspectRatio: aspect,
                  }}
                  className="relative z-10 w-full tablet:max-h-[600px] tablet:w-auto tablet:max-w-[500px] desktop:max-h-[600px] desktop:max-w-[525px] desktop:rounded-[10px]">
                  <video
                    style={{
                      aspectRatio: aspect,
                    }}
                    src={preview_video_url}
                    poster={thumbnail}
                    ref={videoRef}
                    playsInline
                    className="relative z-10 w-full tablet:max-h-[600px] tablet:w-auto tablet:max-w-[500px] desktop:max-h-[600px] desktop:max-w-[525px] desktop:rounded-[10px]"
                  />
                  <VideoProvider
                    remember={[]}
                    element={videoRef}>
                    <div className="absolute bottom-0 left-0 z-50 w-full text-color-white">
                      <Timeline radius="0px 0px 10px 10px" />
                    </div>
                    <PlayWithProvider />
                  </VideoProvider>
                </div>
              ) : (
                <div className="flex w-full tablet:h-[646px] tablet:w-[389px]">
                  <CustomThemedResource
                    source="/general/empty-media"
                    alt="No Video"
                  />
                </div>
              )}
            </div>

            {/* Info Section */}
            <div
              className={clsx(
                "flex w-full flex-col items-center bg-color-popup-cell tablet:p-0 desktop:w-[472px] desktop:bg-[transparent]",
                related.related.length === 0 &&
                  related.searches.length === 0 &&
                  "pb-[90px]",
              )}>
              <div className="no-scrollbar w-full bg-color-popup-cell pt-4 tablet:w-[520px] tablet:py-4 desktop:h-fit desktop:w-full desktop:rounded-[10px]">
                {owner && (
                  <UsernameFollowSection
                    uid={owner.uid}
                    username={owner.username}
                    follow_status={owner.follow_status}
                    profile_pic={
                      owner.profile_pic?.original
                    }
                    followers={owner.followers}
                  />
                )}

                {/* Separator */}
                {owner && (
                  <div className="mx-4 mt-3 h-[1px] flex-1 shrink-0 bg-color-separator" />
                )}

                {/* Session Name */}
                <SessionName
                  title={title}
                  created_at={created_at}
                  hasOwner={owner ? true : false}
                />
                <div className="desktop:hidden">
                  <ShareSection
                    share_url={
                      share_url ??
                      `${APP_HOST}/ad-examples/single/${id}`
                    }
                    videoUrl={preview_video_url}
                    previewImage={thumbnail ?? ""}
                    id={id}
                    title={title}
                  />
                </div>

                {/* Separator */}
                <div className="mx-4 mt-3 h-[1px] flex-1 shrink-0 bg-color-separator" />

                {/* Script Section */}
                <ScriptSection
                  script={props.job.script}
                  description={props.job.description}
                />

                {/* Try Button */}
                <TryNowButton description={description} />
              </div>

              <div className="hidden w-full bg-color-popup-cell desktop:mt-5 desktop:block desktop:h-fit desktop:rounded-[10px] desktop:pb-4">
                {/* Share Section */}
                <ShareSection
                  share_url={
                    share_url ??
                    `${APP_HOST}/ad-examples/single/${id}`
                  }
                  videoUrl={preview_video_url}
                  previewImage={preview_thumbnail_url}
                  id={id}
                  title={title}
                />
              </div>
            </div>
          </div>

          {/* Related Section */}
          <RelatedSectionSinglePage related={related} />
        </div>
      </div>

      <div className="hidden tablet:block desktop:mt-[70px]">
        <WrappedFooter />
      </div>
    </div>
  )
}

interface RelatedSectionProps {
  related: RelatedType
}

function RelatedSectionSinglePage(
  props: RelatedSectionProps,
) {
  const { searches, related } = props.related
  const { t } = useTranslation()
  return (
    <>
      {searches.length !== 0 && (
        <div
          className={clsx(
            "flex w-full flex-col gap-3 px-4 pt-5 desktop:w-[1175px] desktop:px-0 desktop:pt-12",
            related.length === 0 && "pb-[90px]",
          )}>
          <span className="text-[22px] font-500 tracking-normal text-blue-700">
            {t("txt_related_keywords")}
          </span>

          <div className="no-scrollbar flex h-[36px] w-full shrink-0 flex-row gap-[10px] overflow-x-scroll">
            {searches.map((keyword) => (
              <a
                key={keyword}
                href={`/ad-examples/${encodeURIComponent(
                  keyword,
                )}`}
                className={clsx(
                  "flex h-full shrink-0 flex-row items-center justify-center gap-[6px] rounded-[6px]",
                  "bg-color-background p-3 tablet:bg-color-background desktop:bg-color-popup-cell",
                  "text-[13px] font-400 tracking-wider text-blue-700 [--icon-color:var(--color-blue-600)]",
                )}>
                {keyword}
              </a>
            ))}
          </div>
        </div>
      )}

      {related.length !== 0 && (
        <div className="flex w-full flex-col gap-[14px] px-4 pb-[90px] pt-5 desktop:w-[1175px] desktop:px-0 desktop:pb-0">
          <span className="text-[20px] font-500 tracking-normal text-blue-700">
            {t("txt_relevant_ads")}
          </span>

          <ResponsiveMasonry
            columnsCountBreakPoints={{
              1: 3,
              760: 4,
              1280: 5,
            }}>
            <Masonry gutter="10px">
              {related.map((single) => (
                <SingleAd key={single.id} entity={single} />
              ))}
            </Masonry>
          </ResponsiveMasonry>
        </div>
      )}
    </>
  )
}

async function getResult(id: string, token: string) {
  try {
    const response = await axios
      .get<unknown>(
        `${base}/${code}/v2/wizard/get-result`,
        {
          params: {
            id,
          },

          headers: {
            Authorization: `Bearer ${token}`,
            "web-version": HEADER_WEB_VERSION,
          },
        },
      )
      .then((r) => r.data)

    const res = resultSchema.parse(response)

    return res.result
  } catch (e) {
    console.error(e)
    return null
  }
}

const relatedSchema = z.object({
  related: z.array(adExampleEntrySchema),
  searches: z.string().array(),
})

type RelatedType = z.infer<typeof relatedSchema>

async function getRelatedSSR(id: string, token: string) {
  try {
    const response = await axios
      .get(`${base}/${code}/v2/wizard/gallery/related`, {
        params: {
          id,
          limit: 20,
        },

        headers: {
          Authorization: `Bearer ${token}`,
          "web-version": HEADER_WEB_VERSION,
        },
      })
      .then((r) => r.data)

    const result = relatedSchema.parse(response)

    return result
  } catch (e) {
    console.error(e)
    return { related: [], searches: [] }
  }
}

type ResultResponse = z.infer<typeof adExampleEntrySchema>

const resultSchema = z.object({
  result: adExampleEntrySchema,
})

export const getServerSideProps = wrapSSRAuth(
  async (context, userInfo) => {
    const { id } = context.query
    if (typeof id !== "string") {
      throw new SSRNotFound()
    }

    const [job, related] = await Promise.all([
      getResult(id, userInfo.accessToken),
      getRelatedSSR(id, userInfo.accessToken),
    ])

    const props = {
      job,
      related,
    }

    if (!job) {
      throw new SSRNotFound()
    }

    return props
  },
  [],
)
